import React from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import LazyLoad from "react-lazyload";

import Card from "../../components/Card";
import CardSection from "../../components/CardSection";
import CTASection from "../../components/CTASection";

import givingClubs from "../../assets/images/support/givingclubs.webp";
import inKind from "../../assets/images/support/inkind.webp";
import matching from "../../assets/images/support/matching.webp";
import plannedGiving from "../../assets/images/support/plannedgiving.webp";
import teamBuilding from "../../assets/images/support/teambuilding.webp";
import tribute from "../../assets/images/support/tribute.webp";
import unique from "../../assets/images/support/unique.webp";
import special from "../../assets/images/support/specialOccasion.webp";
import athletic from "../../assets/images/support/athletic.webp";

const cardData = [
  {
    title: "Tribute/Memorial",
    description:
      "Create a tribute or memorial page and raise funds in honor or memory of your loved one.",
    image: tribute,
    link: "/support/tribute-memorial-gifts",
  },
  {
    title: "Special Occasion",
    description:
      "Turn your birthday, anniversary, housewarming, or other milestone into hope.",
    image: special,
    link: "/support/special-occasion",
  },
  {
    title: "Athletic",
    description:
      "Use your fitness challenge or sporting event to build homes with Habitat Buffalo.",
    image: athletic,
    link: "/support/athletic",
  },
  {
    title: "Workplace Giving and Matching Gifts",
    description:
      "Support Habitat Buffalo's mission through workplace giving and matching gifts. Your employer's tools can help advance our work.",
    image: matching,
    link: "/support/workplace-giving",
  },
  {
    title: "Gift-In-Kind Donations",
    description:
      "Donate building materials and services to support our construction program and save thousands of dollars each year.",
    image: inKind,
    link: "/support-us/donate-items",
  },
  {
    title: "Team Build Opportunities",
    description:
      "Team Build Days provide unforgettable experiences for your company or faith-based group while supporting our mission.",
    image: teamBuilding,
    link: "/get-involved/team-build-opportunities",
  },
  {
    title: "Planned Giving",
    description:
      "Support Habitat Buffalo through bequests, charitable gift annuities, and charitable remainder trusts while solidifying your financial future.",
    image: plannedGiving,
    link: "/support/planned-giving",
  },
  {
    title: "Giving Clubs",
    description:
      "Join our giving clubs to acknowledge the generosity and impact of our donors on the families we partner with each year.",
    image: givingClubs,
    link: "/support/giving-clubs",
  },
  {
    title: "Get Creative",
    description: "Get creative and raise funds to build community your way.",
    image: unique,
    link: "/support/unique-ways-to-give",
  },
];

const HostYourOwnFundraiser = () => (
  <>
    <Helmet>
      <title>Host Your Own Fundraiser - Habitat for Humanity Buffalo</title>
      <meta
        name="description"
        content="Get creative and host your own fundraiser to support Habitat for Humanity Buffalo. Learn how you can make an impact and help build homes for families in need."
      />
    </Helmet>
    <Container>
      <Title>Host Your Own Fundraiser</Title>
      <SectionHeader className="section-header primary">
        Get Creative And Help Habitat's Mission!
      </SectionHeader>
      <Paragraph>
        Fundraise your way to support the mission of Habitat Buffalo with your
        very own Custom Build fundraiser. Your creativity is the only limit to
        the impact you can have! Whether you are a business, community
        organization, team, or an individual passionate about Habitat Buffalo,
        we can help you put on your own fundraiser. We are always looking for
        new and creative fundraising events.
      </Paragraph>
      <Paragraph>
        Here are some examples of unique fundraisers organized by supporters in
        the past:
      </Paragraph>
      <ul>
        <li>
          <strong>Gaming For Good:</strong> A tabletop gaming event at Raymore
          and Flanagan Outlet.
        </li>
        <li>
          <strong>Community Fun Run:</strong> A neighborhood run with local
          sponsors and fun activities for families.
        </li>
        <li>
          <strong>Art for Homes:</strong> An art auction featuring local artists
          with proceeds going to Habitat Buffalo.
        </li>
      </ul>
      <Section>
        <SectionHeader className="section-header orange">
          We Help You Set Up For Success!
        </SectionHeader>
        <Paragraph>
          At Habitat Buffalo, we are dedicated to making your fundraising event
          a success. Our team is at your disposal to help with marketing, event
          planning, and execution. We provide resources and support to ensure
          your event runs smoothly and achieves its goals. We can help you with:
        </Paragraph>
        <ul>
          <li>Event planning and logistics</li>
          <li>Marketing and promotion</li>
          <li>Volunteer coordination</li>
          <li>Fundraising strategies</li>
          <li>Post-event follow-up and recognition</li>
        </ul>
      </Section>
      <SectionHeader className="section-header green">
        Popular Options For Fundraisers
      </SectionHeader>
      <CardSection>
        {cardData.map((card, index) => (
          <LazyLoad key={index} height={200} offset={100}>
            <Card
              title={card.title}
              description={card.description}
              image={card.image}
              link={card.link}
            />
          </LazyLoad>
        ))}
      </CardSection>
      <CTASection
        title="Let's Get This Party Started!"
        description="Ready to start planning your fundraiser? Reach out to us and let’s make it happen together. Your support will help us build homes, communities, and hope."
        buttonText="Contact Us to Start Planning"
        buttonLink="/form/fundraiser"
      />
    </Container>
  </>
);

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const Title = styled.h1`
  text-align: center;
  margin-bottom: 20px;
`;

const Section = styled.div`
  margin-top: 40px;
`;

const SectionHeader = styled.h2`
  text-align: center;
  background-color: ${(props) => props.color || "var(--primary-color)"};
  color: white;
  padding: 10px;
  border-radius: 5px;
  margin-bottom: 20px;
`;

const Paragraph = styled.p`
  font-size: 1.1em;
  line-height: 1.6;
  margin-bottom: 20px;
`;

export default HostYourOwnFundraiser;
